import React, { MutableRefObject, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Layout from 'shared/layouts/containers/Main'
import { Button, Form, Row } from 'react-bootstrap'
import { useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import InputWrapper from 'shared/components/InputWrapper'
import resAddDevice from './resolvers/resAddDevice'
import useInit from './hooks/useInit'
import useFocusError from 'shared/hooks/useFocusError'
import prepareSubmitForm from './utils/prepareSubmitForm'
import submitForm from './utils/submitForm'
import { Col } from 'reactstrap'
import SelectWrapper from 'shared/components/SelectWrapper'
import useDynamicFilter from 'hooks/useDynamicFilter'
import Comments from './comments'
import { roles } from 'consts'
import hasAnyRole from 'shared/utils/hasAnyRole'
import getBrandContent from 'utils/getBrandContent'
import Footer from 'shared/components/Footer'
import { getWarrantyStatus } from 'pages/Devices/utils/getWarrantyStatus'
import { stringToDate } from 'utils/dateUtils'
import BackButton from 'shared/components/BackButton'

interface AddDeviceProps {
  isReplacement?: boolean
}

const AddDevice: React.FunctionComponent<AddDeviceProps> = (props: AddDeviceProps) => {
  const [showOverlay, setShowOverlay] = useState<boolean>(true)
  const [isDeviceReplacement] = useState<boolean>(props.isReplacement ?? false)

  const [carriers, setCarriers] = useState<any>([])
  const [centers, setCenters] = useState<any>([])
  const [failures, setFailures] = useState<any>([])
  const [manufacturers, setManufacturers] = useState<any>([])
  const [models, setModels] = useState<any>([])
  const [sites, setSites] = useState<any>([])
  const [types, setTypes] = useState<any>([])
  const [serviceStatuses, setServiceStatuses] = useState<any>([])
  const [enableRmaNumber, setEnableRmaNumber] = useState<boolean>(false)
  const [isReplacementServiceStatus, setIsReplacementServiceStatus] = useState<boolean>(false)
  const [enableFailureReason, setEnableFailureReason] = useState<boolean>(false)
  const [currentCustomer, setCurrentCustomer] = useState<{} | null>({})

  const [dataLoaded, setDataLoaded] = useState<boolean>(false)

  const [allModels, setAllModels] = useState<any>([])
  const [allCenters, setAllCenters] = useState<any>([])
  const [currentManufacturer, setCurrentManufacturer] = useState<any>(null)
  const [currentSite, setCurrentSite] = useState<any>(null)
  const [currentFailureReason, setCurrentFailureReason] = useState<any>(undefined)
  const saveAndNewRef: MutableRefObject<boolean> = useRef<boolean>(false)

  const submitRef = React.createRef<HTMLButtonElement>()

  const isSuperAdmin = hasAnyRole([roles.superAdmin])
  const isUser = hasAnyRole([roles.user])

  const { id } = useParams()

  const navigate = useNavigate()

  const {
    control,
    register,
    getValues,
    handleSubmit,
    reset,
    setFocus,
    setValue,
    setError,
    formState: { errors },
  } = useForm({
    mode: 'all',
    resolver: resAddDevice,
  })

  useInit({
    id,
    isDeviceReplacement,
    getValues,
    reset,
    setCarriers,
    setCenters,
    setAllCenters,
    setFailures,
    setManufacturers,
    setCurrentManufacturer,
    setModels,
    setAllModels,
    setSites,
    setCurrentSite,
    setTypes,
    setServiceStatuses,
    setDataLoaded,
    setShowOverlay,
    setEnableRmaNumber,
    setEnableFailureReason,
    setCurrentFailureReason,
    currentCustomer,
    setValue,
    setIsReplacementServiceStatus,
  })

  useDynamicFilter({
    allRows: allModels,
    setFilteredRows: setModels,
    filterColumnName: 'manufacturer_id',
    inputValue: currentManufacturer,
    setDefaultFormFieldValue: setValue,
    formFieldValueName: 'model',
    currentFormValues: getValues(),
    identityColumnName: 'id',
    isFormFieldValueNameObject: true,
    setFirstRecordAsDefault: true,
  })

  useDynamicFilter({
    allRows: allCenters,
    setFilteredRows: setCenters,
    filterColumnName: 'site_id',
    inputValue: currentSite,
    setDefaultFormFieldValue: setValue,
    formFieldValueName: 'center',
    currentFormValues: getValues(),
    identityColumnName: 'id',
    isFormFieldValueNameObject: true,
    setFirstRecordAsDefault: false,
  })

  useFocusError({
    errors,
    setFocus,
  })

  const resetFailureReason = () => {
    setEnableFailureReason(false)
    setValue('failure', '')
    setCurrentFailureReason(undefined)
  }

  return (
    <Layout
      footerContent={<Footer />}
      brandContent={getBrandContent()}
      showOverlay={showOverlay}
      showCardHeader={true}
      showMinimizedBarButton={true}
      headerInsert={<BackButton url='/' text='All Devices' />}
      actionInsert={
        <>
          {!isUser && (
            <>
              <Button
                disabled={!dataLoaded}
                name='save'
                className='btn-round btn-primary me-3'
                onClick={() => {
                  saveAndNewRef.current = false
                  if (submitRef.current != null) {
                    submitRef.current.click()
                  }
                }}
              >
                {isReplacementServiceStatus || props.isReplacement
                  ? 'Replace Device'
                  : 'Save Device'}
              </Button>

              {!isReplacementServiceStatus && !props.isReplacement && (
                <Button
                  disabled={!dataLoaded}
                  name='save'
                  className='btn-round btn-primary'
                  onClick={() => {
                    saveAndNewRef.current = true
                    if (submitRef.current != null) {
                      submitRef.current.click()
                    }
                  }}
                >
                  {isReplacementServiceStatus || props.isReplacement
                    ? 'Replace Device'
                    : 'Save & New'}
                </Button>
              )}
            </>
          )}
        </>
      }
    >
      <>
        <Form
          onSubmit={handleSubmit((data) => {
            const replace: boolean = data?.serviceStatus?.allow_rma_edit === 1
            const initiateReplacement: boolean = data?.serviceStatus?.init_replacement === 1
            setValue('is_first_or_second_replacement_request', replace)
            setValue('is_second_replacement_request', initiateReplacement)
            prepareSubmitForm({
              submitForm,
              navigate,
              data,
              setShowOverlay,
              id,
              successMessage:
                initiateReplacement && id !== undefined
                  ? 'Device successfully saved. Please submit a replacement device.'
                  : props.isReplacement
                  ? 'Device successfully replaced.'
                  : 'Device successfully saved.',
              successNav:
                initiateReplacement && id !== undefined
                  ? '/replace-device/' + id
                  : saveAndNewRef.current === true
                  ? '/add-device'
                  : '/',
              isDeviceReplacement,
              setError,
              initiateReplacement,
              saveAndNewClicked: saveAndNewRef.current === true,
            })
          })}
          id='main-form'
        >
          {id !== undefined && isSuperAdmin && (
            <Row>
              <Col md='6'>
                <InputWrapper
                  colAttributes={{ md: 12 }}
                  label='Customer'
                  name='customer'
                  errors={errors}
                >
                  <Form.Control type='text' disabled={true} {...register('customer')} />
                </InputWrapper>
              </Col>
            </Row>
          )}

          <Row>
            <Col md='6'>
              <InputWrapper
                colAttributes={{ md: 12 }}
                label='MAC Address'
                name='mac_address'
                errors={errors}
              >
                <Form.Control type='text' disabled={isUser} {...register('mac_address')} />
              </InputWrapper>

              <InputWrapper
                colAttributes={{ md: 12 }}
                label='Serial #'
                name='serial_number'
                errors={errors}
              >
                <Form.Control type='text' disabled={isUser} {...register('serial_number')} />
              </InputWrapper>

              <InputWrapper colAttributes={{ md: 12 }} label='Label' name='label' errors={errors}>
                <Form.Control type='text' disabled={isUser} {...register('label')} />
              </InputWrapper>

              <InputWrapper
                colAttributes={{ md: 12 }}
                label='Device Type'
                name='type'
                errors={errors}
              >
                <SelectWrapper
                  name='type'
                  isClearable={false}
                  control={control}
                  options={types}
                  isDisabled={isUser}
                  optionLabel='name'
                  optionValue='id'
                />
              </InputWrapper>

              <InputWrapper
                colAttributes={{ md: 12 }}
                label='Manufacturer'
                name='manufacturer'
                errors={errors}
              >
                <SelectWrapper
                  name='manufacturer'
                  isClearable={false}
                  control={control}
                  options={manufacturers}
                  isDisabled={isUser}
                  optionLabel='name'
                  optionValue='id'
                  placeholder='Select Manufacturer...'
                  onChange={(option) => setCurrentManufacturer(option?.id)}
                />
              </InputWrapper>

              <InputWrapper colAttributes={{ md: 12 }} label='Model' name='model' errors={errors}>
                <SelectWrapper
                  name='model'
                  control={control}
                  isClearable={false}
                  options={models}
                  isDisabled={isUser}
                  optionLabel='name'
                  optionValue='id'
                  placeholder='Select Model...'
                />
              </InputWrapper>

              <InputWrapper
                colAttributes={{ md: 12 }}
                label='Carrier'
                name='carrier'
                errors={errors}
              >
                <SelectWrapper
                  name='carrier'
                  isClearable={false}
                  control={control}
                  options={carriers}
                  isDisabled={isUser}
                  optionLabel='name'
                  optionValue='id'
                  placeholder='Select Carrier...'
                />
              </InputWrapper>

              <InputWrapper colAttributes={{ md: 12 }} label='Site' name='site' errors={errors}>
                <SelectWrapper
                  name='site'
                  isClearable={false}
                  control={control}
                  options={sites}
                  isDisabled={isUser}
                  optionLabel='name'
                  optionValue='id'
                  placeholder='Select Site...'
                  onChange={(option) => setCurrentSite(option?.id)}
                />
              </InputWrapper>

              <InputWrapper
                colAttributes={{ md: 12 }}
                label='Cost Center'
                name='center'
                errors={errors}
              >
                <SelectWrapper
                  name='center'
                  control={control}
                  options={centers}
                  isDisabled={isUser}
                  optionLabel='name'
                  optionValue='id'
                  placeholder='Select Cost Center...'
                />
              </InputWrapper>
            </Col>

            <Col md='6'>
              <InputWrapper
                colAttributes={{ md: 12 }}
                label='Origin RMA #'
                name='origin_rma'
                errors={errors}
              >
                <Form.Control type='text' disabled={true} {...register('origin_rma')} />
              </InputWrapper>

              <InputWrapper colAttributes={{ md: 12 }} label='Owner' name='owner' errors={errors}>
                <Form.Control type='text' disabled={isUser} {...register('owner')} />
              </InputWrapper>

              <InputWrapper
                colAttributes={{ md: 12 }}
                label='Last Used By'
                name='last_used_by'
                errors={errors}
              >
                <Form.Control type='text' disabled={true} {...register('last_used_by')} />
              </InputWrapper>

              <InputWrapper
                colAttributes={{ md: 12 }}
                label='Last Used Date'
                name='last_used_date'
                errors={errors}
              >
                <Form.Control type='text' disabled={true} {...register('last_used_date')} />
              </InputWrapper>

              <InputWrapper
                colAttributes={{ md: 12 }}
                label='Production Date'
                name='production_date'
                errors={errors}
              >
                <Form.Control type='date' disabled={isUser} {...register('production_date')} />
              </InputWrapper>

              <InputWrapper
                colAttributes={{ md: 12 }}
                label='Warranty Expiration'
                name='warranty_expiration_date'
                errors={errors}
              >
                <Form.Control
                  type='date'
                  disabled={isUser}
                  {...register('warranty_expiration_date')}
                  onChange={(e) => {
                    const currDate: Date | null =
                      e.currentTarget.value.trim().length < 1
                        ? null
                        : stringToDate(e.currentTarget.value as string)
                    setValue('warrantyStatus', getWarrantyStatus(currDate))
                  }}
                />
              </InputWrapper>

              <InputWrapper
                colAttributes={{ md: 12 }}
                label='Warranty Status'
                name='warrantyStatus'
                errors={errors}
              >
                <Form.Control type='text' disabled={true} {...register('warrantyStatus')} />
              </InputWrapper>

              <InputWrapper
                colAttributes={{ md: 12 }}
                label='Service Status'
                name='serviceStatus'
                errors={errors}
              >
                <SelectWrapper
                  name='serviceStatus'
                  isClearable={false}
                  control={control}
                  options={serviceStatuses}
                  isDisabled={isUser}
                  optionLabel='name'
                  optionValue='id'
                  placeholder='Select Service Status...'
                  onChange={(option) => {
                    setIsReplacementServiceStatus(option?.init_replacement === 1)
                    const enableRmaEdit: boolean = option?.allow_rma_edit === 1
                    setEnableRmaNumber(enableRmaEdit)
                    if (!enableRmaEdit) {
                      resetFailureReason()
                      setValue('rma', '')
                    }
                  }}
                />
              </InputWrapper>

              <InputWrapper colAttributes={{ md: 12 }} label='RMA #' name='rma' errors={errors}>
                <Form.Control
                  disabled={!enableRmaNumber || isUser}
                  type='text'
                  {...register('rma')}
                  onChange={(e) => {
                    const val: string = e.currentTarget.value ?? ''
                    const enableFaulureReasonEdit: boolean = val.trim().length > 0
                    if (!enableFaulureReasonEdit) {
                      resetFailureReason()
                    } else {
                      setEnableFailureReason(true)
                    }
                  }}
                />
              </InputWrapper>

              <InputWrapper
                colAttributes={{ md: 12 }}
                label='Failure Reason'
                name='failure'
                errors={errors}
              >
                <SelectWrapper
                  name='failure'
                  control={control}
                  options={enableFailureReason ? failures : []}
                  isDisabled={isUser || !enableFailureReason}
                  optionLabel='name'
                  optionValue='id'
                  placeholder='Select Failure Reason...'
                  onChange={(option) => {
                    setCurrentFailureReason(option?.id)
                  }}
                />
              </InputWrapper>
            </Col>
          </Row>

          {id !== undefined && !isDeviceReplacement && (
            <Row>
              <Col md='3'>
                <InputWrapper colAttributes={{ md: 12 }} label='Create Status' name='create_status_label' errors={errors}>
                  <Form.Control type='text' disabled={true} {...register('create_status_label')} />
                </InputWrapper>
              </Col>
              <Col md='3'>
                <InputWrapper colAttributes={{ md: 12 }} label='Create Type' name='create_type_label' errors={errors}>
                  <Form.Control type='text' disabled={true} {...register('create_type_label')} />
                </InputWrapper>
              </Col>
            </Row>
          )}

          <Button type='submit' name='save' ref={submitRef} className='d-none'></Button>
        </Form>

        {id !== undefined && (
          <>
            <hr />
            <div className='mt-3'>
              <Comments id={id} />
            </div>
          </>
        )}
      </>
    </Layout>
  )
}

export default AddDevice
